/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T20:15:00+05:00
Intent: The currency each country pays in (ISO 3166 alpha-2 -> ISO 4217), so a receipt level can offer and default to its campaign countries' currencies, and a country name can be matched back to its code.
*/

/**
 * Main currency per country. Countries that share one (the euro area, the CFA franc zones, the
 * US dollar) are listed one by one, because the campaign's Location is a list of countries.
 */
const COUNTRY_CURRENCY: Record<string, string> = {
  // Gulf and the Middle East
  ae: 'AED', sa: 'SAR', kw: 'KWD', qa: 'QAR', bh: 'BHD', om: 'OMR', ye: 'YER', iq: 'IQD',
  jo: 'JOD', lb: 'LBP', sy: 'SYP', ps: 'ILS', il: 'ILS', ir: 'IRR', tr: 'TRY',
  // North Africa
  eg: 'EGP', ly: 'LYD', tn: 'TND', dz: 'DZD', ma: 'MAD', eh: 'MAD', sd: 'SDG', ss: 'SSP', mr: 'MRU',
  // Rest of Africa
  ng: 'NGN', gh: 'GHS', ke: 'KES', tz: 'TZS', ug: 'UGX', rw: 'RWF', bi: 'BIF', et: 'ETB', er: 'ERN',
  dj: 'DJF', so: 'SOS', za: 'ZAR', na: 'NAD', bw: 'BWP', zw: 'ZWL', zm: 'ZMW', mw: 'MWK', mz: 'MZN',
  ao: 'AOA', cd: 'CDF', mg: 'MGA', mu: 'MUR', sc: 'SCR', km: 'KMF', ls: 'LSL', sz: 'SZL', gm: 'GMD',
  gn: 'GNF', sl: 'SLE', lr: 'LRD', cv: 'CVE', st: 'STN',
  bj: 'XOF', bf: 'XOF', ci: 'XOF', gw: 'XOF', ml: 'XOF', ne: 'XOF', sn: 'XOF', tg: 'XOF',
  cm: 'XAF', cf: 'XAF', td: 'XAF', cg: 'XAF', gq: 'XAF', ga: 'XAF',
  // South and Central Asia
  pk: 'PKR', in: 'INR', bd: 'BDT', lk: 'LKR', np: 'NPR', bt: 'BTN', mv: 'MVR', af: 'AFN',
  kz: 'KZT', uz: 'UZS', kg: 'KGS', tj: 'TJS', tm: 'TMT', az: 'AZN', am: 'AMD', ge: 'GEL',
  // East and South-East Asia, Oceania
  cn: 'CNY', hk: 'HKD', mo: 'MOP', tw: 'TWD', jp: 'JPY', kr: 'KRW', kp: 'KPW', mn: 'MNT',
  sg: 'SGD', my: 'MYR', id: 'IDR', th: 'THB', vn: 'VND', ph: 'PHP', mm: 'MMK', kh: 'KHR',
  la: 'LAK', bn: 'BND', tl: 'USD', au: 'AUD', nz: 'NZD', pg: 'PGK', fj: 'FJD', sb: 'SBD',
  vu: 'VUV', ws: 'WST', to: 'TOP', ki: 'AUD', nr: 'AUD', tv: 'AUD', fm: 'USD', mh: 'USD', pw: 'USD',
  // Europe
  gb: 'GBP', ch: 'CHF', li: 'CHF', no: 'NOK', se: 'SEK', dk: 'DKK', is: 'ISK', pl: 'PLN', cz: 'CZK',
  hu: 'HUF', ro: 'RON', bg: 'BGN', rs: 'RSD', ba: 'BAM', mk: 'MKD', al: 'ALL', md: 'MDL', ua: 'UAH',
  by: 'BYN', ru: 'RUB',
  de: 'EUR', fr: 'EUR', it: 'EUR', es: 'EUR', pt: 'EUR', nl: 'EUR', be: 'EUR', lu: 'EUR', at: 'EUR',
  ie: 'EUR', fi: 'EUR', gr: 'EUR', cy: 'EUR', mt: 'EUR', sk: 'EUR', si: 'EUR', ee: 'EUR', lv: 'EUR',
  lt: 'EUR', hr: 'EUR', mc: 'EUR', sm: 'EUR', va: 'EUR', ad: 'EUR', me: 'EUR', xk: 'EUR',
  // Americas
  us: 'USD', ca: 'CAD', mx: 'MXN', br: 'BRL', ar: 'ARS', cl: 'CLP', co: 'COP', pe: 'PEN', ve: 'VES',
  uy: 'UYU', py: 'PYG', bo: 'BOB', ec: 'USD', sv: 'USD', pa: 'PAB', cr: 'CRC', gt: 'GTQ', hn: 'HNL',
  ni: 'NIO', bz: 'BZD', cu: 'CUP', do: 'DOP', ht: 'HTG', jm: 'JMD', tt: 'TTD', bb: 'BBD', bs: 'BSD',
  gy: 'GYD', sr: 'SRD', pr: 'USD',
  ag: 'XCD', dm: 'XCD', gd: 'XCD', kn: 'XCD', lc: 'XCD', vc: 'XCD',
};

/** The currency a country pays in, by its two-letter code ('eg' -> 'EGP'), or null. */
export function currencyForCountry(iso2: string | null | undefined): string | null {
  return COUNTRY_CURRENCY[String(iso2 ?? '').trim().toLowerCase()] ?? null;
}

/** Every country code this file knows, for matching a country name back to its code. */
export function knownCountryCodes(): string[] {
  return Object.keys(COUNTRY_CURRENCY);
}

let englishNameCodes: Map<string, string> | null = null;
const nameKey = (value: unknown) =>
  String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

/** The two-letter code of an English country name or code ('Egypt' or 'eg' -> 'eg'), or null. */
export function countryCodeForName(name: string | null | undefined): string | null {
  const key = nameKey(name);
  if (!key) return null;
  if (COUNTRY_CURRENCY[key]) return key;
  if (!englishNameCodes) {
    englishNameCodes = new Map();
    try {
      const display = new Intl.DisplayNames(['en'], { type: 'region' });
      for (const code of knownCountryCodes()) {
        englishNameCodes.set(nameKey(display.of(code.toUpperCase())), code);
      }
    } catch { /* no Intl.DisplayNames: only codes match */ }
  }
  return englishNameCodes.get(key) ?? null;
}

/** Every currency some country pays in, sorted. */
export function allCountryCurrencies(): string[] {
  return [...new Set(Object.values(COUNTRY_CURRENCY))].sort();
}

/** Whether this is a real ISO 4217 currency code, not just three letters. */
export function isCurrencyCode(code: string | null | undefined): boolean {
  const c = String(code ?? '').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(c)) return false;
  if (allCountryCurrencies().includes(c)) return true;
  const intl = Intl as any;
  return typeof intl.supportedValuesOf === 'function' && intl.supportedValuesOf('currency').includes(c);
}

/** 'EGP' -> 'Egyptian Pound' (in the given language), or the code itself. */
export function currencyName(code: string, lang = 'en'): string {
  try {
    return new Intl.DisplayNames([lang], { type: 'currency' }).of(code) || code;
  } catch {
    return code;
  }
}
